// Minimap: 180px canvas, terrain-colored downsampled tiles, faction building
// dots, viewport rect, click/drag camera, red raid pings. Redraws at 4fps.
// Owner: client-render.

import { CHUNK, SQRT3, hexToPixel, tileFromIdx } from '../../shared/hex'
import { TERRAIN, Terrain } from '../../shared/terrain'
import { WILD_COLOR } from '../../shared/constants'
import type { GameEvent } from '../../shared/types'
import { HEX_SIZE, type GameState } from '../state'
import { fogOf, fogGround, minimapFog } from './fog'

const SIZE = 180 // css px
const SPAN = 150 * HEX_SIZE * SQRT3 // world px shown edge to edge (~150 tiles)
const K = SIZE / SPAN

let inited = false

/** Ground-plane view size in world px, for the viewport rect. The 2.5D default
 *  derives it from cam.zoom; the 3D renderer passes its own (orbit dist + FOV). */
type ViewGround = () => { w: number; h: number }

export function initMinimap(st: GameState, viewGround?: ViewGround): void {
  if (inited) return
  inited = true

  const flashes: { x: number; y: number; t0: number }[] = []
  st.on('events', (data: unknown) => {
    const list: GameEvent[] = Array.isArray(data) ? data : data ? [data as GameEvent] : []
    for (const ev of list) {
      if ((ev.kind !== 'raid' && ev.kind !== 'combat') || ev.q === undefined || ev.r === undefined) continue
      const p = hexToPixel(ev.q, ev.r, HEX_SIZE)
      flashes.push({ x: p.x, y: p.y, t0: performance.now() })
      if (flashes.length > 12) flashes.shift()
    }
  })

  // ui creates the canvas; poll until it exists
  const attach = (): void => {
    const canvas = document.getElementById('minimap') as HTMLCanvasElement | null
    if (!canvas) {
      setTimeout(attach, 300)
      return
    }
    const dpr = window.devicePixelRatio || 1
    canvas.title = 'Known terrain · gold rings are explorers · green diamonds are discoveries · click to look around'
    canvas.width = SIZE * dpr
    canvas.height = SIZE * dpr
    // Display size comes from CSS (media queries shrink it on phones); the
    // bitmap stays at full SIZE and scales down crisply.
    const ctx = canvas.getContext('2d')!

    // click + drag to move the camera
    let dragging = false
    let centerX = 0
    let centerY = 0
    const moveCam = (ev: MouseEvent): void => {
      const rect = canvas.getBoundingClientRect()
      const mx = ((ev.clientX - rect.left) / Math.max(1, rect.width)) * SIZE
      const my = ((ev.clientY - rect.top) / Math.max(1, rect.height)) * SIZE
      st.cam.x = centerX + (mx - SIZE / 2) / K
      st.cam.y = centerY + (my - SIZE / 2) / K
    }
    canvas.addEventListener('mousedown', (ev) => {
      if (ev.button !== 0) return
      ev.preventDefault()
      dragging = true
      centerX = st.cam.x // map is centered on the camera at drag start
      centerY = st.cam.y
      moveCam(ev)
    })
    window.addEventListener('mousemove', (ev) => {
      if (dragging) moveCam(ev)
    })
    window.addEventListener('mouseup', () => { dragging = false })

    const draw = (): void => {
      const now = performance.now()
      const cx = dragging ? centerX : st.cam.x
      const cy = dragging ? centerY : st.cam.y
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.fillStyle = '#0a1016'
      ctx.fillRect(0, 0, SIZE, SIZE)
      const toMini = (wx: number, wy: number): [number, number] =>
        [SIZE / 2 + (wx - cx) * K, SIZE / 2 + (wy - cy) * K]

      // terrain, per tile, downsampled to ~1.2px cells; fogged tiles draw dimmed
      const fog = fogOf(st)
      const half = SPAN / 2 + HEX_SIZE * CHUNK * 2
      const px = Math.max(1.1, K * HEX_SIZE * SQRT3)
      for (const ck of st.chunks.values()) {
        const c0 = hexToPixel(ck.cx * CHUNK + CHUNK / 2, ck.cy * CHUNK + CHUNK / 2, HEX_SIZE)
        if (Math.abs(c0.x - cx) > half || Math.abs(c0.y - cy) > half) continue
        for (let i = 0; i < ck.tiles.length; i++) {
          const t = tileFromIdx(ck.cx, ck.cy, i)
          if (!fogGround(fog, t.q, t.r)) continue // never-seen ground stays void
          const p = hexToPixel(t.q, t.r, HEX_SIZE)
          const [mx, my] = toMini(p.x, p.y)
          if (mx < -1 || mx > SIZE + 1 || my < -1 || my > SIZE + 1) continue
          ctx.fillStyle = TERRAIN[ck.tiles[i] as Terrain].color
          if (minimapFog(fog, t.q, t.r)) ctx.globalAlpha = 0.42
          ctx.fillRect(mx - px / 2, my - px / 2, px + 0.4, px + 0.4)
          ctx.globalAlpha = 1
        }
        // faint territory tint
        for (const [idx, owner] of ck.owners) {
          const t = tileFromIdx(ck.cx, ck.cy, idx)
          if (!fogGround(fog, t.q, t.r)) continue
          const p = hexToPixel(t.q, t.r, HEX_SIZE)
          const [mx, my] = toMini(p.x, p.y)
          if (mx < -1 || mx > SIZE + 1 || my < -1 || my > SIZE + 1) continue
          ctx.globalAlpha = 0.4
          ctx.fillStyle = st.factions.get(owner)?.color ?? '#b0a48c'
          ctx.fillRect(mx - px / 2, my - px / 2, px + 0.4, px + 0.4)
          ctx.globalAlpha = 1
        }
      }

      // building dots in faction colors
      for (const b of st.buildings.values()) {
        if (b.type === 'plant') continue // saplings are vegetation, not village structures
        const p = hexToPixel(b.q, b.r, HEX_SIZE)
        const [mx, my] = toMini(p.x, p.y)
        if (mx < 0 || mx > SIZE || my < 0 || my > SIZE) continue
        const color = b.owner === 'wild' ? WILD_COLOR : st.factions.get(b.owner)?.color ?? '#b0a48c'
        const d = b.type === 'townhall' ? 3.4 : 2
        if (!b.done) {
          // construction sites: hollow outline dots
          ctx.strokeStyle = color
          ctx.lineWidth = 0.8
          ctx.strokeRect(mx - d / 2, my - d / 2, d, d)
          continue
        }
        ctx.fillStyle = color
        ctx.fillRect(mx - d / 2, my - d / 2, d, d)
        if (b.type === 'townhall') {
          ctx.strokeStyle = 'rgba(255,255,255,0.7)'
          ctx.lineWidth = 0.7
          ctx.strokeRect(mx - d / 2, my - d / 2, d, d)
        }
      }

      // Remember useful places after the explorer has moved on.
      for (const find of st.self?.camp?.discoveries ?? []) {
        const p = hexToPixel(find.q, find.r, HEX_SIZE)
        const [mx, my] = toMini(p.x, p.y)
        if (mx < 3 || mx > SIZE - 3 || my < 3 || my > SIZE - 3) continue
        ctx.fillStyle = '#91e1b5'; ctx.strokeStyle = '#183b2d'; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(mx, my - 3.5); ctx.lineTo(mx + 3.5, my); ctx.lineTo(mx, my + 3.5); ctx.lineTo(mx - 3.5, my); ctx.closePath(); ctx.fill(); ctx.stroke()
      }

      // Explorers stay easy to locate as their sight uncovers more terrain.
      for (const u of st.units.values()) {
        if (u.owner !== st.me || !u.exploring || u.hp <= 0) continue
        const p = hexToPixel(u.q, u.r, HEX_SIZE)
        const [mx, my] = toMini(p.x, p.y)
        if (mx < 0 || mx > SIZE || my < 0 || my > SIZE) continue
        ctx.strokeStyle = '#f3d489'
        ctx.lineWidth = 1.2
        ctx.beginPath()
        ctx.arc(mx, my, 3.4, 0, Math.PI * 2)
        ctx.stroke()
        ctx.fillStyle = '#fff2c2'
        ctx.fillRect(mx - 1, my - 1, 2, 2)
      }

      // red ping flashes for raids/combat
      for (let i = flashes.length - 1; i >= 0; i--) {
        const f = flashes[i]
        const age = (now - f.t0) / 3000
        if (age >= 1) { flashes.splice(i, 1); continue }
        const [mx, my] = toMini(f.x, f.y)
        if (mx < -8 || mx > SIZE + 8 || my < -8 || my > SIZE + 8) continue
        const blink = 0.5 + 0.5 * Math.sin(now * 0.02)
        ctx.strokeStyle = `rgba(255,70,55,${(1 - age) * (0.5 + 0.5 * blink)})`
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.arc(mx, my, 3 + age * 5, 0, Math.PI * 2)
        ctx.stroke()
      }

      // viewport rect (main canvas assumed fullscreen)
      const vg = viewGround
        ? viewGround()
        : { w: window.innerWidth / st.cam.zoom, h: window.innerHeight / st.cam.zoom }
      const vw = vg.w * K
      const vh = vg.h * K
      const [vx, vy] = toMini(st.cam.x, st.cam.y)
      ctx.strokeStyle = 'rgba(240,244,248,0.85)'
      ctx.lineWidth = 1
      ctx.strokeRect(vx - vw / 2, vy - vh / 2, vw, vh)

      // frame
      ctx.strokeStyle = 'rgba(255,255,255,0.15)'
      ctx.strokeRect(0.5, 0.5, SIZE - 1, SIZE - 1)
    }

    draw()
    setInterval(draw, 250) // 4fps, not rAF
  }
  attach()
}
